import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";

import { isApiClientError } from "../api/errors";
import type { PostApiV1SiteNewsBody } from "../api/generated";
import { resolveApiUrl } from "../config/environment";
import type { SiteContentNews } from "../entities/site-content";
import { SiteMediaUpload } from "./SiteContentPages";
import { CloseButton } from "./CloseButton";
import type { ShellServices } from "./services";
import { EmptyState, LoadingPage } from "./states";
import { Select } from "./Select";
import { Notice } from "./Notice";

type Status = SiteContentNews["status"] | "";
type ContentFormat = "html" | "json";
type FormValues = {
  title: string;
  slug: string;
  excerpt: string;
  contentFormat: ContentFormat;
  content: string;
  categoryId: string;
  coverMediaId: string;
  alt: string;
  seoTitle: string;
  seoDescription: string;
};

const emptyForm: FormValues = {
  title: "",
  slug: "",
  excerpt: "",
  contentFormat: "json",
  content: "[]",
  categoryId: "",
  coverMediaId: "",
  alt: "",
  seoTitle: "",
  seoDescription: "",
};

function date(value: string | null): string {
  return value
    ? new Intl.DateTimeFormat("ru-RU", {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(new Date(value))
    : "—";
}

function message(error: unknown): string {
  if (isApiClientError(error)) {
    if (error.status === 409)
      return "Изменения не сохранены: эту новость уже обновил другой редактор. Откройте актуальную версию, сравните данные и повторите действие.";
    if (error.status === 422)
      return "Проверьте обязательные поля и формат контента. Сервер не сохранил изменения.";
    if (error.status === 403) return "Недостаточно прав для этого действия.";
  }
  return "Операция не выполнена. Повторите попытку.";
}

function formFrom(news: SiteContentNews): FormValues {
  return {
    title: news.title,
    slug: news.slug,
    excerpt: news.excerpt,
    contentFormat: news.content_format,
    content:
      news.content_format === "html"
        ? (news.content_html ?? "")
        : JSON.stringify(news.content ?? [], null, 2),
    categoryId: news.category?.id ?? "",
    coverMediaId: news.cover?.id ?? "",
    alt: news.cover?.alt ?? "",
    seoTitle: news.seo.title ?? "",
    seoDescription: news.seo.description ?? "",
  };
}

function parseContent(
  values: FormValues,
): Pick<PostApiV1SiteNewsBody, "content_html" | "content"> {
  if (values.contentFormat === "html") return { content_html: values.content };
  try {
    const content: unknown = JSON.parse(values.content || "[]");
    if (!Array.isArray(content)) throw new Error("not array");
    return {
      content: content as NonNullable<PostApiV1SiteNewsBody["content"]>,
    };
  } catch {
    throw new Error("JSON-контент должен быть массивом блоков.");
  }
}

type NewsBlock = string | Record<string, unknown>;
type NewsImage = { readonly src: string; readonly alt?: string };

function isNewsImage(value: unknown): value is NewsImage {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }

  return typeof (value as Record<string, unknown>)["src"] === "string";
}

function blocksFromContent(content: string): NewsBlock[] {
  try {
    const parsed: unknown = JSON.parse(content || "[]");
    return Array.isArray(parsed) ? (parsed as NewsBlock[]) : [];
  } catch {
    return [];
  }
}

function ContentBlocksEditor({
  content,
  onChange,
  pendingMedia,
  onInsertMedia,
}: {
  content: string;
  onChange: (next: string) => void;
  pendingMedia: { id: string; url: string | null; alt: string | null } | null;
  onInsertMedia: (index: number) => void;
}) {
  const blocks = blocksFromContent(content);
  const saveBlocks = (next: NewsBlock[]) =>
    onChange(JSON.stringify(next, null, 2));
  const addBlock = (block: NewsBlock) => saveBlocks([...blocks, block]);
  const updateBlock = (index: number, block: NewsBlock) => {
    const next = [...blocks];
    next[index] = block;
    saveBlocks(next);
  };
  return (
    <div className="news-composer">
      <div className="news-composer__toolbar">
        <strong>Материал</strong>
        <div
          className="news-composer__tools"
          role="group"
          aria-label="Добавить блок"
        >
          <button
            className="button button--secondary button--compact"
            type="button"
            onClick={() => addBlock("")}
          >
            Абзац
          </button>
          <button
            className="button button--secondary button--compact"
            type="button"
            onClick={() => addBlock({ type: "subtitle", text: "" })}
          >
            Подзаголовок
          </button>
          <button
            className="button button--secondary button--compact"
            type="button"
            onClick={() => addBlock({ type: "quote", text: "", author: "" })}
          >
            Цитата
          </button>
        </div>
      </div>
      {blocks.length === 0 ? (
        <button
          className="news-composer__empty"
          type="button"
          onClick={() => saveBlocks([""])}
        >
          Начните с первого абзаца
        </button>
      ) : null}
      {blocks.map((block, index) => (
        <div className="news-block" key={`${typeof block}-${index}`}>
          {pendingMedia ? (
            <button
              className="news-block__insert"
              type="button"
              onClick={() => onInsertMedia(index)}
            >
              Вставить изображение сюда
            </button>
          ) : null}
          {typeof block === "string" ? (
            <textarea
              aria-label={`Абзац ${index + 1}`}
              value={block}
              placeholder="Введите текст…"
              onChange={(event) => updateBlock(index, event.target.value)}
            />
          ) : block.type === "image" ? (
            <figure>
              <img
                src={
                  typeof block.src === "string" ? resolveSitePreview(block.src) : ""
                }
                alt={typeof block.alt === "string" ? block.alt : ""}
              />
              <figcaption>
                {typeof block.alt === "string" ? block.alt : "Изображение"}
              </figcaption>
            </figure>
          ) : block.type === "subtitle" ? (
            <input
              aria-label={`Подзаголовок ${index + 1}`}
              value={typeof block.text === "string" ? block.text : ""}
              placeholder="Подзаголовок"
              onChange={(event) =>
                updateBlock(index, { ...block, text: event.target.value })
              }
            />
          ) : block.type === "quote" ? (
            <div className="news-block__quote-fields">
              <textarea
                aria-label={`Цитата ${index + 1}`}
                value={typeof block.text === "string" ? block.text : ""}
                placeholder="Текст цитаты"
                onChange={(event) =>
                  updateBlock(index, { ...block, text: event.target.value })
                }
              />
              <input
                aria-label={`Автор цитаты ${index + 1}`}
                value={typeof block.author === "string" ? block.author : ""}
                placeholder="Автор (необязательно)"
                onChange={(event) =>
                  updateBlock(index, { ...block, author: event.target.value })
                }
              />
            </div>
          ) : (
            <pre>{JSON.stringify(block, null, 2)}</pre>
          )}
          <div className="news-block__actions">
            <button
              className="button button--text"
              type="button"
              disabled={index === 0}
              onClick={() => {
                const next = [...blocks];
                [next[index - 1], next[index]] = [
                  next[index]!,
                  next[index - 1]!,
                ];
                saveBlocks(next);
              }}
            >
              Выше
            </button>
            <button
              className="button button--text"
              type="button"
              disabled={index === blocks.length - 1}
              onClick={() => {
                const next = [...blocks];
                [next[index], next[index + 1]] = [
                  next[index + 1]!,
                  next[index]!,
                ];
                saveBlocks(next);
              }}
            >
              Ниже
            </button>
            <button
              className="button button--text button--danger"
              type="button"
              onClick={() =>
                saveBlocks(
                  blocks.filter((_, blockIndex) => blockIndex !== index),
                )
              }
            >
              Удалить
            </button>
          </div>
        </div>
      ))}
      {pendingMedia ? (
        <button
          className="news-block__insert news-block__insert--end"
          type="button"
          onClick={() => onInsertMedia(blocks.length)}
        >
          Вставить изображение в конец
        </button>
      ) : null}
    </div>
  );
}

function NewsLivePreview({
  title,
  excerpt,
  content,
}: {
  title: string;
  excerpt: string;
  content: string;
}) {
  return (
    <aside className="news-live-preview" aria-label="Предпросмотр новости">
      <p className="eyebrow">Предпросмотр на сайте</p>
      <h2>{title || "Заголовок новости"}</h2>
      <p className="news-live-preview__excerpt">
        {excerpt || "Анонс появится здесь"}
      </p>
      {blocksFromContent(content).map((block, index) => {
        if (typeof block === "string")
          return <p key={index}>{block || "Текст абзаца"}</p>;
        if (block.type === "image" && typeof block.src === "string")
          return (
            <figure key={index}>
              <img
                src={resolveSitePreview(block.src)}
                alt={typeof block.alt === "string" ? block.alt : ""}
              />
              <figcaption>
                {typeof block.alt === "string" ? block.alt : ""}
              </figcaption>
            </figure>
          );
        if (block.type === "subtitle" && typeof block.text === "string")
          return <h3 key={index}>{block.text || "Подзаголовок"}</h3>;
        if (block.type === "quote" && typeof block.text === "string")
          return (
            <blockquote key={index}>
              <p>{block.text || "Текст цитаты"}</p>
              {typeof block.author === "string" && block.author ? (
                <footer>{block.author}</footer>
              ) : null}
            </blockquote>
          );
        if (block.type === "list" && Array.isArray(block.items))
          return (
            <ul key={index}>
              {block.items
                .filter((item): item is string => typeof item === "string")
                .map((item, itemIndex) => (
                  <li key={itemIndex}>{item}</li>
                ))}
            </ul>
          );
        if (block.type === "gallery" && Array.isArray(block.images))
          return (
            <div className="news-live-preview__gallery" key={index}>
              {block.images.filter(isNewsImage).map((image, imageIndex) => (
                <img
                  key={imageIndex}
                  src={resolveSitePreview(image.src)}
                  alt={typeof image.alt === "string" ? image.alt : ""}
                />
              ))}
            </div>
          );
        return null;
      })}
    </aside>
  );
}

function NewsForm({
  news,
  onClose,
  services,
}: {
  news: SiteContentNews | null;
  onClose: () => void;
  services: ShellServices;
}) {
  const client = useQueryClient();
  const api = services.siteContent!;
  const [values, setValues] = useState<FormValues>(() =>
    news ? formFrom(news) : emptyForm,
  );
  const [notice, setNotice] = useState<string | null>(null);
  const [lastMedia, setLastMedia] = useState<{
    id: string;
    url: string | null;
    alt: string | null;
  } | null>(null);
  const insertMedia = (
    media: {
      id: string;
      url: string | null;
      alt: string | null;
    },
    index: number,
  ) => {
    try {
      const parsed: unknown = JSON.parse(values.content || "[]");
      if (!Array.isArray(parsed)) throw new Error();
      const position = Math.max(0, Math.min(index, parsed.length));
      parsed.splice(position, 0, {
        type: "image",
        src: media.url ?? `/api/public/v1/media/${media.id}`,
        alt: media.alt ?? "",
      });
      setValues({ ...values, content: JSON.stringify(parsed, null, 2) });
      setLastMedia(media);
      setNotice(`Медиа вставлено в блок ${position + 1}.`);
    } catch {
      setNotice(
        "Сначала исправьте JSON-контент: он должен быть массивом блоков.",
      );
    }
  };
  const save = useMutation({
    mutationFn: () => {
      const content = parseContent(values);
      const common = {
        title: values.title.trim(),
        excerpt: values.excerpt.trim(),
        content_format: values.contentFormat,
        ...content,
        category_id: values.categoryId || null,
        cover_media_id: values.coverMediaId || null,
        seo_title: values.seoTitle || null,
        seo_description: values.seoDescription || null,
      };
      return news
        ? api.updateNews(news.id, {
            ...common,
            ...(values.slug.trim() ? { slug: values.slug.trim() } : {}),
            version: news.version,
          })
        : api.createNews({ ...common, slug: values.slug.trim() || null });
    },
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["site", "news"] });
      onClose();
    },
    onError: (error) =>
      setNotice(
        error instanceof Error && error.message.startsWith("JSON")
          ? error.message
          : message(error),
      ),
  });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    setNotice(null);
    save.mutate();
  };
  return (
    <section className="site-editor" aria-labelledby="site-news-editor-title">
      <header className="page-heading page-heading--split">
        <div>
          <p className="eyebrow">Публичный сайт</p>
          <h1 id="site-news-editor-title">
            {news ? "Редактор новости" : "Новая новость"}
          </h1>
          <p>
            Черновик остаётся непубличным. Публикация, снятие и расписание
            выполняются отдельными действиями.
          </p>
        </div>
        <button
          className="button button--secondary"
          type="button"
          onClick={onClose}
        >
          К списку
        </button>
      </header>
      <form className="site-form" onSubmit={submit}>
        <label>
          <span>Заголовок</span>
          <input
            required
            maxLength={255}
            value={values.title}
            onChange={(event) =>
              setValues({ ...values, title: event.target.value })
            }
          />
        </label>
        <label>
          <span>Slug</span>
          <input
            pattern="[a-z0-9]+(-[a-z0-9]+)*"
            value={values.slug}
            onChange={(event) =>
              setValues({ ...values, slug: event.target.value.toLowerCase() })
            }
            placeholder="оставьте пустым для генерации"
          />
        </label>
        <label className="site-form__wide">
          <span>Анонс</span>
          <textarea
            required
            maxLength={2000}
            value={values.excerpt}
            onChange={(event) =>
              setValues({ ...values, excerpt: event.target.value })
            }
          />
        </label>
        <label>
          <span>Формат</span>
          <Select
            value={values.contentFormat}
            onChange={(event) =>
              setValues({
                ...values,
                contentFormat: event.target.value as ContentFormat,
                content: "",
              })
            }
          >
            <option value="html">HTML</option>
            <option value="json">JSON-блоки</option>
          </Select>
        </label>
        <label>
          <span>Категория</span>
          <input
            value={values.categoryId}
            onChange={(event) =>
              setValues({ ...values, categoryId: event.target.value })
            }
            placeholder="UUID категории"
          />
        </label>
        <div className="site-form__wide news-editor-layout">
          {values.contentFormat === "json" ? (
            <ContentBlocksEditor
              content={values.content}
              onChange={(content) => setValues({ ...values, content })}
              pendingMedia={null}
              onInsertMedia={(index) => {
                if (lastMedia) insertMedia(lastMedia, index);
              }}
            />
          ) : (
            <label>
              <span>HTML-контент</span>
              <textarea
                required
                className="site-form__content"
                value={values.content}
                onChange={(event) =>
                  setValues({ ...values, content: event.target.value })
                }
              />
            </label>
          )}
          {values.contentFormat === "json" ? (
            <NewsLivePreview
              title={values.title}
              excerpt={values.excerpt}
              content={values.content}
            />
          ) : null}
        </div>
        <fieldset className="site-form__wide news-media-composer">
          <legend>Медиа в тексте</legend>
          {values.contentFormat === "json" ? (
            <>
              <p>
                Загрузите изображение — оно сразу появится в конце материала и в
                preview. Используйте «Выше» и «Ниже» у блока, чтобы поставить
                его на нужное место.
              </p>
              <div className="news-media-composer__controls">
                <SiteMediaUpload
                  services={api}
                  selectedMediaId={lastMedia?.id}
                  onSelected={(media) => {
                    insertMedia(
                      media,
                      blocksFromContent(values.content).length,
                    );
                  }}
                />
              </div>
            </>
          ) : (
            <p>
              Для размещения медиа внутри текста выберите формат «JSON-блоки».
              HTML остаётся доступным для уже подготовленных материалов.
            </p>
          )}
        </fieldset>
        <fieldset className="site-form__wide">
          <legend>Обложка и SEO</legend>
          <div className="site-form">
            <div className="cover-media-control">
              <label>
                <span>ID подтверждённого медиа</span>
                <input
                  value={values.coverMediaId}
                  onChange={(event) =>
                    setValues({ ...values, coverMediaId: event.target.value })
                  }
                  placeholder="UUID медиа"
                />
              </label>
              {lastMedia ? (
                <button
                  className="button button--text"
                  type="button"
                  onClick={() =>
                    setValues({
                      ...values,
                      coverMediaId: lastMedia.id,
                      alt: lastMedia.alt ?? "",
                    })
                  }
                >
                  Использовать последнее медиа как обложку
                </button>
              ) : null}
            </div>
            <label>
              <span>Alt-текст</span>
              <input
                value={values.alt}
                onChange={(event) =>
                  setValues({ ...values, alt: event.target.value })
                }
                placeholder="Описание изображения"
              />
            </label>
            <label>
              <span>SEO title</span>
              <input
                maxLength={255}
                value={values.seoTitle}
                onChange={(event) =>
                  setValues({ ...values, seoTitle: event.target.value })
                }
              />
            </label>
            <label>
              <span>SEO description</span>
              <textarea
                maxLength={500}
                value={values.seoDescription}
                onChange={(event) =>
                  setValues({ ...values, seoDescription: event.target.value })
                }
              />
            </label>
          </div>
        </fieldset>
        {notice ? <Notice>{notice}</Notice> : null}
        <div className="dw-form-actions">
          <button
            className="button button--primary"
            disabled={save.isPending}
            type="submit"
          >
            {save.isPending
              ? "Сохраняем…"
              : news
                ? "Сохранить версию"
                : "Создать черновик"}
          </button>
          <button
            className="button button--secondary"
            type="button"
            onClick={onClose}
          >
            Отмена
          </button>
        </div>
      </form>
    </section>
  );
}

function Preview({
  id,
  onClose,
  services,
}: {
  id: string;
  onClose: () => void;
  services: ShellServices;
}) {
  const preview = useQuery({
    queryKey: ["site", "news", id, "preview"],
    queryFn: () => services.siteContent!.previewNews(id),
    retry: false,
  });
  const news = preview.data?.data;
  return (
    <div
      className="modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="site-preview-title"
    >
      <button
        className="modal-backdrop"
        type="button"
        aria-label="Закрыть предпросмотр"
        onClick={onClose}
      />
      <section className="modal-card site-preview">
        <header className="modal-title">
          <div>
            <p className="eyebrow">Серверный preview</p>
            <h2 id="site-preview-title">{news?.title ?? "Предпросмотр"}</h2>
          </div>
          <CloseButton label="Закрыть предпросмотр" onClick={onClose} />
        </header>
        {preview.isPending ? <p>Готовим безопасный preview…</p> : null}
        {preview.isError ? <Notice>{message(preview.error)}</Notice> : null}
        {news?.content_format === "html" ? (
          <iframe
            title="Предпросмотр новости"
            sandbox=""
            srcDoc={news.content_html ?? ""}
          />
        ) : null}
        {news?.content_format === "json" ? (
          <NewsLivePreview
            title={news.title}
            excerpt={news.excerpt}
            content={JSON.stringify(news.content ?? [])}
          />
        ) : null}
      </section>
    </div>
  );
}

export function SiteNewsPage({ services }: { services: ShellServices }) {
  const client = useQueryClient();
  const api = services.siteContent!;
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<Status>("");
  const [categoryId, setCategoryId] = useState("");
  const [author, setAuthor] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [sort, setSort] = useState("-created_at");
  const [page, setPage] = useState(1);
  const [editor, setEditor] = useState<SiteContentNews | null | "new">(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const news = useQuery({
    queryKey: [
      "site",
      "news",
      { search, status, categoryId, author, from, to, sort, page },
    ],
    queryFn: () =>
      api.listNews({
        page,
        per_page: 20,
        ...(search ? { search } : {}),
        ...(status ? { status } : {}),
        ...(categoryId ? { category_id: categoryId } : {}),
        ...(author ? { author } : {}),
        ...(from ? { published_from: new Date(from).toISOString() } : {}),
        ...(to ? { published_to: new Date(to).toISOString() } : {}),
        sort: sort as "-created_at",
      }),
    retry: false,
  });
  const categories = useQuery({
    queryKey: ["site", "categories"],
    queryFn: () => api.listCategories(),
    retry: false,
  });
  const invalidate = () =>
    client.invalidateQueries({ queryKey: ["site", "news"] });
  const action = useMutation({
    mutationFn: async ({
      type,
      item,
      scheduledAt,
    }: {
      type: "publish" | "unpublish" | "archive" | "schedule";
      item: SiteContentNews;
      scheduledAt?: string;
    }) => {
      if (type === "publish") return api.publishNews(item.id);
      if (type === "unpublish") return api.unpublishNews(item.id);
      if (type === "archive") return api.deleteNews(item.id);
      return api.scheduleNews(item.id, {
        version: item.version,
        scheduled_at: new Date(scheduledAt ?? "").toISOString(),
      });
    },
    onSuccess: async () => {
      await invalidate();
      setNotice("Статус новости обновлён.");
    },
    onError: (error) => setNotice(message(error)),
  });
  if (editor !== null)
    return (
      <NewsForm
        news={editor === "new" ? null : editor}
        services={services}
        onClose={() => setEditor(null)}
      />
    );
  if (news.isPending) return <LoadingPage label="Загружаем новости" />;
  if (news.isError)
    return (
      <section className="workspace-page">
        <EmptyState
          title="Не удалось загрузить новости"
          description={message(news.error)}
        />
      </section>
    );
  const list = news.data.data;
  return (
    <section
      className="workspace-page site-news-page"
      aria-labelledby="site-news-title"
    >
      <header className="page-heading page-heading--split">
        <div>
          <p className="eyebrow">Публичный сайт</p>
          <h1 id="site-news-title">Новости</h1>
          <p>
            Управляйте черновиками, публикациями и запланированными материалами.
          </p>
        </div>
        <button
          className="button button--primary"
          type="button"
          onClick={() => setEditor("new")}
        >
          Создать новость
        </button>
      </header>
      {notice ? (
        <p className="operation-notice" role="status">
          {notice}
        </p>
      ) : null}
      <div className="users-toolbar site-filters" aria-label="Фильтры новостей">
        <label>
          <span>Поиск</span>
          <input
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
          />
        </label>
        <label>
          <span>Статус</span>
          <Select
            value={status}
            onChange={(event) => {
              setStatus(event.target.value as Status);
              setPage(1);
            }}
          >
            <option value="">Все</option>
            <option value="draft">Черновик</option>
            <option value="scheduled">Запланирована</option>
            <option value="published">Опубликована</option>
            <option value="archived">Архив</option>
          </Select>
        </label>
        <label>
          <span>Автор</span>
          <input
            value={author}
            onChange={(event) => {
              setAuthor(event.target.value);
              setPage(1);
            }}
          />
        </label>
        <label>
          <span>Категория</span>
          <Select
            value={categoryId}
            onChange={(event) => {
              setCategoryId(event.target.value);
              setPage(1);
            }}
          >
            <option value="">Все категории</option>
            {(categories.data?.data ?? []).map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </Select>
        </label>
        <label>
          <span>С</span>
          <input
            type="date"
            value={from}
            onChange={(event) => {
              setFrom(event.target.value);
              setPage(1);
            }}
          />
        </label>
        <label>
          <span>По</span>
          <input
            type="date"
            value={to}
            onChange={(event) => {
              setTo(event.target.value);
              setPage(1);
            }}
          />
        </label>
        <label>
          <span>Сортировка</span>
          <Select
            value={sort}
            onChange={(event) => setSort(event.target.value)}
          >
            <option value="-created_at">Сначала новые</option>
            <option value="created_at">Сначала старые</option>
            <option value="-published_at">По публикации</option>
            <option value="title">По названию</option>
          </Select>
        </label>
      </div>
      {list.length === 0 ? (
        <EmptyState
          title="Новостей не найдено"
          description="Измените фильтры или создайте первый черновик."
        />
      ) : (
        <div className="users-table-wrap">
          <table className="users-table site-news-table">
            <thead>
              <tr>
                <th>Материал</th>
                <th>Статус</th>
                <th>Категория</th>
                <th>Публикация</th>
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {list.map((item) => (
                <tr key={item.id}>
                  <td data-label="Материал">
                    <strong>{item.title}</strong>
                    <small>
                      v{item.version} · {item.slug}
                    </small>
                  </td>
                  <td data-label="Статус">
                    <span
                      className={`status-badge status-badge--${item.status}`}
                    >
                      {item.status}
                    </span>
                  </td>
                  <td data-label="Категория">{item.category?.name ?? "—"}</td>
                  <td data-label="Публикация">
                    {date(item.published_at ?? item.scheduled_at)}
                  </td>
                  <td data-label="Действия" className="inline-actions">
                    <button
                      className="button button--secondary button--compact"
                      type="button"
                      onClick={() => setEditor(item)}
                    >
                      Редактировать
                    </button>
                    <button
                      className="button button--text"
                      type="button"
                      onClick={() => setPreview(item.id)}
                    >
                      Preview
                    </button>
                    {item.status !== "published" &&
                    item.status !== "archived" ? (
                      <button
                        className="button button--text"
                        disabled={action.isPending}
                        type="button"
                        onClick={() => action.mutate({ type: "publish", item })}
                      >
                        Опубликовать
                      </button>
                    ) : null}
                    {item.status === "published" ? (
                      <button
                        className="button button--text"
                        disabled={action.isPending}
                        type="button"
                        onClick={() =>
                          action.mutate({ type: "unpublish", item })
                        }
                      >
                        Снять
                      </button>
                    ) : null}
                    {item.status === "draft" ? (
                      <button
                        className="button button--text"
                        disabled={action.isPending}
                        type="button"
                        onClick={() => {
                          const scheduledAt = window.prompt(
                            "Дата и время публикации (локальное):",
                            "",
                          );
                          if (scheduledAt)
                            action.mutate({
                              type: "schedule",
                              item,
                              scheduledAt,
                            });
                        }}
                      >
                        Запланировать
                      </button>
                    ) : null}
                    {item.status !== "archived" ? (
                      <button
                        className="button button--text button--danger"
                        disabled={action.isPending}
                        type="button"
                        onClick={() => {
                          if (window.confirm(`Архивировать «${item.title}»?`))
                            action.mutate({ type: "archive", item });
                        }}
                      >
                        В архив
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="audit-pagination">
        <button
          className="button button--secondary"
          disabled={page === 1}
          type="button"
          onClick={() => setPage(page - 1)}
        >
          Назад
        </button>
        <span>Страница {news.data.meta.current_page}</span>
        <button
          className="button button--secondary"
          disabled={news.data.meta.current_page >= news.data.meta.last_page}
          type="button"
          onClick={() => setPage(page + 1)}
        >
          Далее
        </button>
      </div>
      {preview ? (
        <Preview
          id={preview}
          services={services}
          onClose={() => setPreview(null)}
        />
      ) : null}
    </section>
  );
}

/** Черновики рисуются через сессионный endpoint; в тексте сохраняется постоянный адрес. */
function resolveSitePreview(source: string): string {
  return resolveApiUrl(source.replace(/\/api\/public\/v1\/media\/([0-9a-f-]{36})(?=\?|$)/i, "/api/v1/site/media/$1/content"));
}
